/**
 * «Занято»: что делать с приглашением, которое пришло, пока телефон уже занят
 * другим звонком.
 *
 * <p><b>Чего не было раньше.</b> Занятости не существовало вовсе. Единственный
 * `call_line_busy` в приложении означал совсем другое — «на прямой звонок
 * ответили дважды» (см. {@link decideAccept}). Всё остальное проходило насквозь:
 *
 * <ul>
 *   <li>звонок в <b>другом чате</b> посреди разговора показывал полноэкранный
 *       входящий поверх живого звонка, а рингтон играл в ту же сессию, в
 *       которой шёл разговор;</li>
 *   <li>второй звонящий в <b>тот же чат</b> молча отбрасывался
 *       ({@link isParallelInvite}) — звонящий при этом слышал гудки до
 *       упора и не понимал, почему ему не отвечают;</li>
 *   <li>два абонента, набравшие друг друга <b>одновременно</b>, вообще не
 *       узнавали о встречном вызове: у обоих фаза `calling`, приглашение друг
 *       друга каждый выбрасывал, и оба слушали гудки в пустоту до таймаута.</li>
 * </ul>
 *
 * <p>Правила чистые: снимок своего состояния плюс разобранное приглашение — на
 * выходе решение. Ни рефов, ни сети, поэтому перебираются тестами целиком.
 */

import type { CallPhase, CallScope } from './callSignalRules';

/** Ключ чата, устойчивый к регистру и форме UUID (WS и навигация расходятся). */
export function normCallChatId(v: string | null | undefined): string {
  return typeof v === 'string' ? v.trim().replace(/-/g, '').toLowerCase() : '';
}

/** Звонок, которым телефон занят прямо сейчас. */
export type ActiveCallSnapshot = {
  chatId: string;
  /** «Нога» звонка. Может быть ещё не привязана — тогда null. */
  callInviteId: string | null;
  phase: CallPhase;
  scope: CallScope;
};

/** Разобранное входящее приглашение. */
type IncomingInviteSnapshot = {
  chatId: string;
  callInviteId: string | null;
  scope: CallScope;
};

/**
 * Почему линия занята.
 *
 * <p>Различать нужно не ради текста: `glare` — встречный вызов, и он кладёт
 * трубку <b>обеим</b> сторонам, тогда как остальные причины трогают только
 * звонящего.
 */
type BusyReason = 'in_call' | 'other_chat' | 'parallel_ring' | 'glare';

type BusyDecision =
  /** Свободны — звонить можно. */
  | { action: 'ring' }
  /** Это наш же звонок (эхо приглашения, повтор по WS+push) — молча мимо. */
  | { action: 'ignore'; reason: 'own_call' }
  /** Линия занята: показывать входящий нельзя, звонящему уходит `call_line_busy`. */
  | { action: 'busy'; reason: BusyReason };

/**
 * Занята ли линия для этого приглашения.
 *
 * <p>Порядок проверок важен, и первым идёт «это вообще не наш ли собственный
 * звонок?»: одно и то же приглашение доходит и по WS, и пушем, и ответить на
 * второй экземпляр «занято» значило бы уронить звонок, которого мы же и ждём.
 * Ошибка в эту сторону дороже: пропущенное «занято» — это лишние гудки,
 * лишнее «занято» — это несостоявшийся разговор.
 */
export function decideIncomingInviteWhileBusy(
  active: ActiveCallSnapshot | null,
  invite: IncomingInviteSnapshot,
): BusyDecision {
  if (!active || active.phase === 'idle') {
    return { action: 'ring' };
  }
  if (normCallChatId(active.chatId) !== normCallChatId(invite.chatId)) {
    return { action: 'busy', reason: 'other_chat' };
  }
  if (active.callInviteId && invite.callInviteId && active.callInviteId === invite.callInviteId) {
    return { action: 'ignore', reason: 'own_call' };
  }
  /**
   * Групповой звонок в том же чате занятостью не бывает. Активная сессия в чате
   * на сервере одна, и одновременный старт двумя участниками сводится к ней же
   * (`startOrJoin` идемпотентен) — то есть встречное приглашение здесь не
   * конфликт, а тот же самый разговор с другой ногой.
   */
  if (active.scope === 'group' && invite.scope === 'group') {
    return { action: 'ignore', reason: 'own_call' };
  }
  /**
   * Нога своего звонка ещё не привязана, а мы принимающая сторона — это и есть
   * то самое приглашение, которого мы ждём: у прямых звонков `inviteId` в
   * маршруте экрана бывает пуст, и привязка происходит как раз отсюда.
   * Считать его вторым звонящим значило бы отвечать «занято» своему же
   * собеседнику и не соединиться никогда.
   */
  if (active.phase === 'ringing' && !active.callInviteId) {
    return { action: 'ring' };
  }
  if (active.phase === 'incall') {
    return { action: 'busy', reason: 'in_call' };
  }
  if (active.phase === 'calling') {
    // Встречный вызов: мы набираем того же собеседника, который набирает нас.
    return { action: 'busy', reason: 'glare' };
  }
  return { action: 'busy', reason: 'parallel_ring' };
}

/**
 * Кладёт ли это «занято» трубку и нам самим.
 *
 * <p>Только встречный вызов. Во всех остальных случаях занят собеседник, а наш
 * собственный разговор идёт и обрывать его нельзя — иначе входящий звонок
 * посреди разговора убивал бы разговор, ради которого его и отклонили.
 */
export function busyEndsOurOwnCall(reason: BusyReason): boolean {
  return reason === 'glare';
}
